import {capitalize} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {InlineLinkButton} from '../../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {openInBrowser} from '../../../../../../srcCommon/browser/openInBrowser';
import {
  CrmSyncErrorCodesEnum,
  CrmSyncErrorModel,
  CrmSyncSourcesEnum,
} from '../../../../../models/api/crmSyncModel';
import {IconBox} from '../../../../common/iconBox/iconBox';
import {LinkToHubspot} from '../../hubspot/hubspotHelpers';
import {
  buildErrorCodeMap,
  computeAccountSyncErrorCodeTitle,
  computeHelpCenterLink,
  maxErrorCountToShow,
} from '../crmSyncHelpers';
import {CrmSyncErrorList} from './crmSyncErrorList';

/*
 * Types.
 */

interface CrmSyncErrorsPageProps {
  errors: ReadonlyArray<CrmSyncErrorModel>;
  onBackToMainPage: Handler;
  source: CrmSyncSourcesEnum;
  externalUrl?: string;
  navigateToLink: HandlerOf<string>;
}

/*
 * Style.
 */

const StyledErrorListWrapperDiv = styled.div`
  padding-bottom: 15px;
`;
const StyledContainerDiv = styled.div`
  padding: 20px;
`;

const StyledEmptyStateContainerDiv = styled.div`
  text-align: center;
  margin: 100px 0px;
`;

const StyledIcon = styled(Icon)`
  display: inline;
  margin-left: 4px;
`;

const StyledInlineLinkButton = styled(InlineLinkButton)`
  display: flex;
  align-items: center;
`;

const StyledErrorTitleWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
`;

const StyledErrorTitleDiv = styled.div`
  font-weight: ${fontWeights.bold};
`;

const StyledLinkDiv = styled.div`
  padding-left: 28px;
  margin-top: 10px;
  font-weight: 600;
`;

const StyledLearnMoreButton = styled(InlineLinkButton)`
  display: inline-block;
  margin-left: auto;
`;

const StyledLearnMoreContentDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.bold};
`;

const StyledLearnMoreIcon = styled(Icon)`
  margin-left: 4px;
`;

const StyledInfoBannerIconBox = styled(IconBox)`
  font-size: ${fontSizes.verySmall};
`;

/*
 * Component.
 */

export const CrmSyncErrorsPage: FC<CrmSyncErrorsPageProps> = (props) => {
  const {source, externalUrl, errors, onBackToMainPage, navigateToLink} = props;

  const errorCodeMap = buildErrorCodeMap(errors);

  const errorCodes = Object.keys(errorCodeMap);
  const showEmptyState = errorCodes.length === 0;

  const onLinkClick = (isLinkToModal: boolean) => {
    if (isLinkToModal) {
      return onBackToMainPage();
    }
    if (externalUrl && source === CrmSyncSourcesEnum.HUBSPOT) {
      return navigateToLink(LinkToHubspot);
    }
    if (externalUrl) {
      return navigateToLink(externalUrl);
    }
    return undefined;
  };

  const onErrorClick = (externalResourceId: string) => {
    navigateToLink(`${externalUrl}/${externalResourceId}`);
  };

  const onLearnMoreClick = () => {
    openInBrowser(computeHelpCenterLink(source));
  };

  return (
    <StyledContainerDiv>
      {errorCodes.map((code) => (
        <StyledErrorListWrapperDiv>
          {renderErrorTitle(code, errorCodeMap[code].length, capitalize(source))}
          <CrmSyncErrorList code={code} errors={errorCodeMap[code]} onErrorClick={onErrorClick} />
          {maybeRenderLink(code, source, onLinkClick, errorCodeMap[code].length)}
        </StyledErrorListWrapperDiv>
      ))}

      {showEmptyState && renderEmptyState()}
      {renderInfoBanner(source, onLearnMoreClick)}
    </StyledContainerDiv>
  );
};

/*
 * Helpers.
 */

function renderErrorTitle(code: string, numErrors: number, source: string) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledErrorTitleWrapperDiv>
          <Icon color={theme.palette.orange.shade40} name="warningFilledSmall" />
          <StyledErrorTitleDiv>
            {computeAccountSyncErrorCodeTitle(code, numErrors, source)}
          </StyledErrorTitleDiv>
        </StyledErrorTitleWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderLink(code: string, source: string, onLinkClick: HandlerOf<boolean>, numErrors: number) {
  if (code === CrmSyncErrorCodesEnum.CRM_FIELD_NOT_FOUND) {
    return (
      <StyledLinkDiv>
        <InlineLinkButton onClick={() => onLinkClick(true)}>
          <Trans id="crm-sync-errors-link-to-edit-field">Edit field</Trans>
        </InlineLinkButton>
      </StyledLinkDiv>
    );
  }

  if (numErrors <= maxErrorCountToShow) {
    return null;
  }
  return (
    <StyledLinkDiv>
      <StyledInlineLinkButton onClick={() => onLinkClick(false)}>
        <Trans id="crm-sync-errors-link-to-crm">View more in</Trans> {` ${capitalize(source)}`}
        <StyledIcon name="externalLinkSmall" />
      </StyledInlineLinkButton>
    </StyledLinkDiv>
  );
}

function renderEmptyState() {
  return (
    <StyledEmptyStateContainerDiv>
      <Trans id="crm-sync-errors-table-empty-state">No errors!</Trans>
    </StyledEmptyStateContainerDiv>
  );
}

function renderInfoBanner(source: CrmSyncSourcesEnum, onLearnMoreClick: Handler) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledInfoBannerIconBox iconName="infoFilledSmall" iconColor={theme.palette.blue.shade40}>
          <Trans id="crm-sync-errors-table-info-banner-part-1">Learn how to best set up your</Trans>
          {` ${capitalize(source)} `}
          <Trans id="crm-sync-errors-table-info-banner-part-2">accounts to sync with Front</Trans>
          <StyledLearnMoreButton onClick={onLearnMoreClick}>
            <StyledLearnMoreContentDiv>
              <Trans id="crm-sync-errors-table-info-banner-learn-more">LEARN MORE</Trans>
              <StyledLearnMoreIcon name="externalLinkSmall" />
            </StyledLearnMoreContentDiv>
          </StyledLearnMoreButton>
        </StyledInfoBannerIconBox>
      )}
    </UserThemeConsumer>
  );
}
